import { createFileRoute, notFound } from "@tanstack/react-router";
import { useState } from "react";
import {
  getInsightAdmin,
  updateInsight,
  generateInsightSlug,
  duplicateInsight,
} from "@/lib/api/insights.functions";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { InsightsAdminNav } from "@/components/admin/InsightsAdminNav";
import { SlugField } from "@/components/admin/SlugField";
import { ContentAdminTabs } from "@/components/admin/ContentAdminTabs";
import { BlockDocEditor } from "@/components/admin/doc/BlockDocEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import type { ArticleBlock } from "@/lib/types/content";
import { MediaField } from "@/components/admin/MediaPicker";
import { coverFromInsight, ogFromInsight, type MediaSelection } from "@/lib/media/types";
import { getPublishedLeaders } from "@/lib/api/public.functions";
import { listInsightTagsAdmin } from "@/lib/api/insight-tags.functions";
import { getInsightImpressions } from "@/lib/api/analytics.functions";
import { INSIGHT_FILTER_CATEGORIES } from "@/lib/insights/categories";
import { InsightTagPicker } from "@/components/admin/InsightTagPicker";
import { resolveEditorDocForLoad } from "@/lib/doc/doc-convert";
import type { EditorDoc } from "@/lib/doc/doc-types";

function toDateInputValue(value: Date | string | null | undefined): string {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

export const Route = createFileRoute("/admin/insights/$id")({
  loader: async ({ params }) => {
    const [insight, publishedLeaders, impressionStats, tagCatalog] = await Promise.all([
      getInsightAdmin({ data: { id: params.id } }),
      getPublishedLeaders(),
      getInsightImpressions({ data: { insightId: params.id } }),
      listInsightTagsAdmin(),
    ]);
    return {
      insight,
      publishedLeaders,
      impressions: impressionStats.impressions,
      tagOptions: tagCatalog
        .filter((tag) => tag.status === "published")
        .map((tag) => ({ name: tag.name, featured: tag.featured })),
    };
  },
  component: EditInsightPage,
});

function EditInsightPage() {
  const { insight, publishedLeaders, impressions, tagOptions } = Route.useLoaderData();
  if (!insight) throw notFound();

  const [title, setTitle] = useState(insight.title);
  const [slug, setSlug] = useState(insight.slug);
  const [category, setCategory] = useState(insight.category ?? "");
  const [tags, setTags] = useState<string[]>(insight.tags ?? []);
  const [excerpt, setExcerpt] = useState(insight.excerpt ?? "");
  const [authorSlug, setAuthorSlug] = useState(insight.authorSlug ?? "");
  const [authorName, setAuthorName] = useState(insight.authorName ?? "");
  const [authorDesignation, setAuthorDesignation] = useState(insight.authorDesignation ?? "");
  const [status, setStatus] = useState(insight.status);
  const [publishedAt, setPublishedAt] = useState(toDateInputValue(insight.publishedAt));
  const [editorDoc, setEditorDoc] = useState<EditorDoc>(() =>
    resolveEditorDocForLoad({
      editorDoc: insight.editorDoc,
      craftState: insight.craftState,
      contentBlocks: (insight.contentBlocks ?? []) as ArticleBlock[],
      contentHtml: insight.contentHtml,
    }),
  );
  const [cover, setCover] = useState<MediaSelection>(coverFromInsight(insight));
  const [ogImage, setOgImage] = useState<MediaSelection>(ogFromInsight(insight));
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await updateInsight({
        data: {
          id: insight.id,
          title,
          slug,
          category,
          tags,
          excerpt,
          status,
          publishedAt: publishedAt || undefined,
          editorDoc,
          coverMediaId: cover.mediaId,
          coverUrl: cover.url,
          ogImageMediaId: ogImage.mediaId,
          authorSlug: authorSlug || undefined,
          authorName: authorName || undefined,
          authorDesignation: authorDesignation || undefined,
        },
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  const categoryOptions = [
    ...INSIGHT_FILTER_CATEGORIES,
    ...(category && !INSIGHT_FILTER_CATEGORIES.includes(category as (typeof INSIGHT_FILTER_CATEGORIES)[number])
      ? [category]
      : []),
  ];

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Edit insight"
        action={
          <Button variant="outline" onClick={() => duplicateInsight({ data: { id: insight.id } }).then(() => toast.success("Duplicated as draft"))}>
            Duplicate
          </Button>
        }
      />
      <InsightsAdminNav />
      <ContentAdminTabs
        settingsLabel="Insight settings"
        settingsForm={
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/40 px-4 py-3">
              <p className="text-sm text-muted-foreground">Total impressions</p>
              <p className="text-2xl font-semibold">{impressions.toLocaleString()}</p>
            </div>
            <div className="space-y-2"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} /></div>
            <SlugField title={title} slug={slug} onSlugChange={setSlug} onGenerate={() => generateInsightSlug({ data: { title, excludeId: insight.id } })} />
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Publish date</Label>
              <Input
                type="date"
                value={publishedAt}
                onChange={(e) => setPublishedAt(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Shown on the public site. Leave empty to use today when first publishing.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Topic tags</Label>
              <InsightTagPicker options={tagOptions} value={tags} onChange={setTags} />
            </div>
            <div className="space-y-2"><Label>Excerpt</Label><Textarea value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={2} /></div>
            <div className="space-y-2">
              <Label>Author</Label>
              <Select
                value={authorSlug || "__none__"}
                onValueChange={(v) => {
                  if (v === "__none__") {
                    setAuthorSlug("");
                    setAuthorName("");
                    setAuthorDesignation("");
                    return;
                  }
                  const leader = publishedLeaders.find((l) => l.slug === v);
                  if (leader) {
                    setAuthorSlug(leader.slug);
                    setAuthorName(leader.name);
                    setAuthorDesignation(leader.designation);
                  }
                }}
              >
                <SelectTrigger><SelectValue placeholder="Select author" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No author</SelectItem>
                  {publishedLeaders.map((l) => (
                    <SelectItem key={l.slug} value={l.slug}>{l.name} — {l.designation}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <MediaField label="Cover image" value={cover} onChange={setCover} />
              <MediaField label="OG image" value={ogImage} onChange={setOgImage} />
            </div>
          </div>
        }
        content={
          <BlockDocEditor
            editorKey={insight.id}
            initialDoc={editorDoc}
            onChange={setEditorDoc}
            className="w-full"
          />
        }
      />
      <AdminFormFooter cancelTo="/admin/insights" onSave={handleSave} saving={saving} />
    </AdminPageShell>
  );
}
